import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import profile from 'src/store/profile';
import api from 'src/utils/api';
// @mui
import { Alert, Container, Card, Stack, Box, Typography, Button, Snackbar, CircularProgress, Grid } from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import { fToNow } from '../../utils/formatTime';

// ----------------------------------------------------------------------

export default function CalendarPage() {
  const [groups, setGroups] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [loadingRecommended, setLoadingRecommended] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleEventClick = () => {
    // navigate('/teacher/user', { state: { ...event.extendedProps, id: event.id } })
  }

  useEffect(() => {
    api.get(`sgroup-api/view?id=${profile.data.sgroup_id}`)
      .then((result) => {
        
        if (Array.isArray(result)) {
          setGroups(result.map((item) => {
            item.title = item.name;
            item.startRecur = item.date_start;
            item.endRecur = item.date_end;
            item.daysOfWeek = [item.first_day, item.second_day];
            return item;
          }));
        } else {
          result.title = result.name;
          result.startRecur = result.date_start;
          result.endRecur = result.date_end;
          result.daysOfWeek = [result.first_day, result.second_day];
          setGroups([result]); // все одно передаємо масив
        }
      })
      .catch(() => {
        setGroups([]);
      });
  }, [])
  
  // Fetch recommended lessons (by analogy with sgroup-api)
  useEffect(() => {
    // Assumption: recommended-api endpoint returns either an array or single object similar to sgroup-api
    // We use profile.data.id (student id) to request recommendations. If your API expects a different param,
    // update the URL accordingly.
    const urlPath = `recommended-api?expand=lesson&filter[student_id]=${profile.data.id}&filter[status]=0&per-page=2`;

    setLoadingRecommended(true);
    api.get(urlPath)
      .then((result) => {
        
        if (Array.isArray(result)) {
          // Map API shape to UI-friendly fields. Example item shape includes:
          // { aprove_date, create_date, group_id, id, lesson_id, price, status, student_id, studio_id, text }
          setRecommended(result.map(item => ({
            ...item,
            // title from lesson.name when available
            title: item.lesson && item.lesson.name ? item.lesson.name : (item.text && item.text.trim() ? item.text : `Урок #${item.lesson_id}`),
            // description from lesson.info (hide price as requested)
            description: item.lesson && item.lesson.info ? item.lesson.info : '',
            // relative create_date for meta display
            dateText: item.create_date ? fToNow(item.create_date) : '',
            // mark requested if status is non-zero
            requested: item.status && Number(item.status) !== 0,
          })));
        } else if (result) {
          const item = result;
          setRecommended([ {
            ...item,
            title: item.lesson && item.lesson.name ? item.lesson.name : (item.text && item.text.trim() ? item.text : `Урок #${item.lesson_id}`),
            description: item.lesson && item.lesson.info ? item.lesson.info : '',
            dateText: item.create_date ? fToNow(item.create_date) : '',
            requested: item.status && Number(item.status) !== 0,
          } ]);
        }
      })
      .catch(() => {
        setRecommended([]);
      })
      .finally(() => setLoadingRecommended(false));
  }, [])

  // Handle user clicking "Хочу" (I want this recommended lesson)
  const handleWant = (rec) => {
    // Optimistic UI: mark as requested locally
    setRecommended(prev => prev.map(r => r.id === rec.id ? { ...r, requested: true } : r));

    // POST status update to API. Adjust endpoint and payload to match your backend.
    const payload = { status: 1 };

    api.patch(`recommended-api/${rec.id}`, payload)
      .then(() => {
        setSnackbar({ open: true, message: 'Запит успішно надіслано', severity: 'success' });
      })
      .catch((err) => {
        api.logError('status update failed', err);
        // rollback optimistic update on error
        setRecommended(prev => prev.map(r => r.id === rec.id ? { ...r, requested: false } : r));
        setSnackbar({ open: true, message: 'Помилка при відправці запиту', severity: 'error' });
      });
  }
  return (
    <>
      <Helmet>
        <title> Розклад | Lala English </title>
      </Helmet>

      <Alert severity="error" sx={{ mb: 1, pl: 3 }}>В розкладі зображений навчальний план студента.</Alert>

  {/* Recommended lessons moved to top of page */}

      <Container>
        {/* Render recommended lessons here so alignment matches calendar */}
        {(loadingRecommended || (recommended && recommended.length > 0)) && (
          <Card sx={{ width: '100%', mb: 3 }}>
            <Stack spacing={2} sx={{ p: 3 }}>
              <Typography variant="h4" sx={{ mb: 1, fontWeight: 800 }}>Рекомендовані уроки</Typography>
              {loadingRecommended ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Grid container spacing={0}>
                  {recommended.map(rec => (
                    <Grid item xs={12} md={6} key={rec.id} sx={{ p: 1 }}>
                      <Box
                        sx={theme => ({
                          position: 'relative',
                          display: 'block',
                          gap: 2,
                          p: 2,
                          border: `1px solid ${rec.requested ? theme.palette.success.main : theme.palette.divider}`,
                          boxShadow: rec.requested ? `0 0 0 4px ${theme.palette.success[50]}` : 'none',
                          borderRadius: 1,
                          minHeight: 120,
                          bgcolor: 'background.paper'
                        })}
                      >
                        <Box sx={{ pr: 1, mt: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{rec.title}</Typography>
                          {rec.description ? (
                            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>{rec.description}</Typography>
                          ) : null}
                          {rec.dateText ? (
                            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>{rec.dateText}</Typography>
                          ) : null}
                        </Box>

                        <Box sx={theme => ({ position: 'absolute', top: theme.spacing(2), right: theme.spacing(2), display: 'flex', alignItems: 'center' })}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleWant(rec)}
                            disabled={Boolean(rec.requested || (rec.status && Number(rec.status) !== 0))}
                            sx={theme => ({
                              width: 120,
                              minWidth: 120,
                              justifyContent: 'center',
                              bgcolor: rec.requested ? theme.palette.success.main : theme.palette.primary.main,
                              color: '#fff',
                              '&:hover': { bgcolor: rec.requested ? theme.palette.success.dark : theme.palette.primary.dark },
                              boxShadow: 2,
                            })}
                          >
                            {rec.requested ? 'Доданий' : 'Хочу'}
                          </Button>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Stack>
          </Card>
        )}

        <Card>
          <Stack spacing={2} sx={{ p: 3 }}>
            {/* calendar only here; recommended list has been moved to the top of the page */}
            <FullCalendar
              plugins={[dayGridPlugin]}
              eventClick={handleEventClick}
              initialView="dayGridMonth"
              weekends
              events={groups}
              locale='uk'
              buttonText={{
                today: 'Сьогодні'
              }}
            />
          </Stack>
        </Card>
      </Container>

      <Alert severity="error" sx={{ mb: 1, pl: 3, whiteSpace: 'pre-line' }}>
        •	зелений колір означає, що урок було відвідано студентом<br />
        •	червоний колір використовується для уроків-бустерів. Це  додаткові рекомендовані уроки вчителем для особистого прогресу студента. Уроки-бустери автоматично наповнюють навчальний шлях дитини можливостями ще більше використовувати англійську і це дає вау результати.<br />
        •	синій колір показує, що урок був невідвіданий і потребує відпрацювання.</Alert>
      <Alert severity="error" sx={{ mb: 5, pl: 3 }}>Систематичні відвідування уроків створюють причину успішного результату кожного студента.</Alert>


      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
